import React, { useState, useEffect } from "react";
import { Modal, TextField, Select, CircularProgress } from "@mui/material";
import { useUserData } from "components/customHooks/userDataContext";
import {
  displayDateFormatter,
  AMPMTime,
  isoDateFormatter,
  addMinutes,
} from "components/helpers";
import PatientPicker from "components/UI/PatientPicker";
import {
  createAppointmentProvider,
  updateAppointmentProvider,
} from "apiHandler/actions";
import { status_array, confirm_status_array } from "utils/constants";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { DATE_TIME_FORMAT } from "utils/helper";

const appointment_type_array = [
  "FOLLOW-UP VISIT",
  "WELLNESS EXAM",
  "URGENT VISIT",
  "ULTRASOUND",
  "NEW PATIENT VISIT",
  "PAP",
  "NO FAULT/ WORKERS COMP",
  "WALK IN",
  "VIDEO VISIT",
  "NURSING ONLY",
];

const initial_error = {
  patient: false,
  provider: false,
  location: false,
  dos: false,
};
const AddAppointment = ({ onClose, appointemnt_data, default_date }) => {
  const userData = useUserData();
  const practice_providers = userData?.practice_providers || [];
  const practice_locations = userData?.practice_locations || [];
  const [error, setError] = useState({ ...initial_error });
  const [loading, setLoading] = useState(false);

  // Input states
  const [appt_type, setAppt_type] = useState("FOLLOW-UP VISIT");
  const [dos, setDos] = useState(default_date || new Date());
  const [duration, setDuration] = useState(15);
  const [status, setStatus] = useState("PENDING ARRIVAL");
  const [confirm_status, setConfirm_status] = useState("Not Confirmed");
  const [chief_complaint, setChief_complaint] = useState("");
  const [patient, setPatient] = useState({});
  const [practice_loc_id, setPractice_loc_id] = useState(
    practice_locations?.[0]?.["practice_loc_id"] || ""
  );
  const [selected_provider, setSelected_provider] = useState(0);
  const [location, setLocation] = useState(
    practice_providers?.[0]?.["city"] || ""
  );
  const [npi, setNpi] = useState(practice_providers?.[0]?.["npi"] || "");
  const [provider_name, setProvider_name] = useState(
    practice_providers?.[0]?.["full_name"] || ""
  );
  const token = userData?.access_token;

  useEffect(() => {
    if (appointemnt_data) {
      setAppt_type(appointemnt_data.appt_type);
      setDos(
        appointemnt_data.dos
          ? `${isoDateFormatter(appointemnt_data?.dos)}T${addMinutes(
              appointemnt_data?.dos_time,
              0
            )}`
          : null
      );
      setDuration(appointemnt_data?.duration || 15);
      setStatus(appointemnt_data?.status || "PENDING ARRIVAL");
      setConfirm_status(appointemnt_data?.confirm_status || "Not Confirmed");
      setChief_complaint(appointemnt_data?.chief_complaint || "");
      setPatient({
        patient_id: appointemnt_data.patient_id,
        first_name: appointemnt_data.first_name,
        last_name: appointemnt_data.last_name,
        dob: appointemnt_data.dob,
        mobile: appointemnt_data.mobile,
        home: appointemnt_data.home,
        full_name: appointemnt_data.patient_name,
      });
      setPractice_loc_id(
        appointemnt_data.practice_loc_id ||
          practice_locations?.[0]?.["practice_loc_id"] ||
          ""
      );
      const index = practice_providers.findIndex(
        (p) =>
          p.npi === appointemnt_data?.npi &&
          p.full_name?.toLowerCase() ===
            appointemnt_data?.provider_name?.toLowerCase() &&
          p.city.toLowerCase() === appointemnt_data?.location?.toLowerCase()
      );
      setSelected_provider(index);
      setLocation(practice_providers?.[index]?.["city"] || "");
      setNpi(practice_providers?.[index]?.["npi"] || "");
      setProvider_name(practice_providers?.[index]?.["full_name"] || "");
    }
  }, [appointemnt_data]);

  const validate = () => {
    let isValid = true;
    let errorstate = { ...initial_error };
    if (!patient.patient_id) {
      isValid = false;
      errorstate.patient = true;
    }
    if (!practice_loc_id) {
      isValid = false;
      errorstate.location = true;
    }
    if (selected_provider < 0) {
      isValid = false;
      errorstate.provider = true;
    }
    if (!dos) {
      isValid = false;
      errorstate.dos = true;
    }
    setError(errorstate);
    return isValid;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    const payload = {
      practice_id: userData.selected_practice,
      dos: displayDateFormatter(dos),
      dos_time: AMPMTime(dos),
      duration,
      chief_complaint,
      patient_id: patient.patient_id,
      first_name: patient.first_name,
      last_name: patient.last_name,
      dob: displayDateFormatter(patient.dob),
      mobile: patient.phone_no_mobile,
      home: patient.phone_no_home,
      status,
      confirm_status,
      appt_type,
      provider_name: provider_name,
      location,
      practice_loc_id,
      npi,
    };
    if (!appointemnt_data) {
      const response = await createAppointmentProvider(payload, token);
      if (response) onClose(true);
    } else {
      payload["id"] = appointemnt_data?.id;
      const response = await updateAppointmentProvider(payload, token);
      if (response) onClose(true);
    }
    setLoading(false);
  };

  return (
    <Modal open={true} onClose={() => {}}>
      <form className="add-appointment-modal">
        <h1>Add Appointment</h1>
        <div className="input-div">
          <div className="label">Appointment Type</div>
          <div className="input">
            <Select
              native
              variant="standard"
              size="small"
              value={appt_type}
              onChange={(e) => setAppt_type(e.target.value)}
            >
              {appointment_type_array.map((s) => (
                <option key={s} value={s} className="text-capitalize">
                  {s.toLowerCase()}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="input-div">
          <div className="label">Appointment Time</div>
          <div className="input">
            <DateTimePicker
              value={dayjs(dos)}
              onChange={(e) => setDos(e)}
              showTodayButton
              error={error.dos}
              format={DATE_TIME_FORMAT}
              slotProps={{ textField: { variant: "standard" } }}
            />
          </div>
        </div>
        <div className="input-div">
          <div className="label">Duration </div>
          <div className="input">
            <Select
              native
              variant="standard"
              value={duration}
              size="small"
              onChange={(e) => setDuration(e.target.value)}
            >
              {["15", "20", "30", "40"].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="input-div">
          <div className="label"> Status </div>
          <div className="input">
            <Select
              native
              value={status}
              variant="standard"
              size="small"
              onChange={(e) => setStatus(e.target.value)}
              disabled={appointemnt_data ? false : true}
            >
              {status_array.map((s) => (
                <option key={s} value={s} className="text-capitalize">
                  {s.toLowerCase()}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="input-div">
          <div className="label">Confirm Status </div>
          <div className="input">
            <Select
              native
              variant="standard"
              size="small"
              value={confirm_status}
              onChange={(e) => setConfirm_status(e.target.value)}
            >
              {confirm_status_array.map((s) => (
                <option key={s} value={s} className="text-capitalize">
                  {s}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="input-div">
          <div className="label"> Chief Complaint</div>
          <div className="input">
            <TextField
              value={chief_complaint}
              onChange={(e) => setChief_complaint(e.target.value)}
            />
          </div>
        </div>
        <div className="input-div">
          <div className="label"> Patient </div>
          <div className="input">
            <PatientPicker
              value={patient}
              setValue={setPatient}
              error={error.patient}
            />
          </div>
        </div>
        <div className="input-div">
          <div className="label"> Location </div>
          <div className="input">
            <Select
              native
              value={practice_loc_id}
              variant="standard"
              size="small"
              onChange={(e) => setPractice_loc_id(e.target.value)}
              error={error.location}
            >
              {practice_loc_id === "" ? <option value=""></option> : null}
              {practice_locations.map((s) => (
                <option
                  key={s.practice_loc_id}
                  value={s.practice_loc_id}
                  className="text-capitalize"
                >
                  {s.city}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="input-div">
          <div className="label"> Provider </div>
          <div className="input">
            <Select
              native
              value={selected_provider}
              variant="standard"
              size="small"
              onChange={(e) => {
                const index = e.target.value;
                const practice = practice_providers[index];
                setSelected_provider(index);
                setNpi(practice.npi);
                setLocation(practice.city);
                setProvider_name(practice.full_name);
              }}
              error={error.provider}
            >
              {practice_providers.map((s, i) => (
                <option key={i} value={i} className="text-capitalize">
                  {s.full_name} - {s.city}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="btn-cont">
          <button type="submit" onClick={onSubmit} className="save">
            {loading ? (
              <CircularProgress color="#fff" size={16} />
            ) : appointemnt_data ? (
              "Update"
            ) : (
              "Save"
            )}
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              if (loading) return;
              onClose();
            }}
            className="cancel"
          >
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddAppointment;
